import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CalendarPlus } from "lucide-react";

export default function VisitRequestModal({ open, onOpenChange, defaultChild, onCreated }) {
  const empty = { child_name: defaultChild || "", visit_date: "", start_time: "", end_time: "", location: "", notes: "", visit_type: "supervised", recurrence: "none", occurrences: 4 };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, occurrences: parseInt(form.occurrences, 10) || 1 };
      const { data } = await api.post("/visits", payload);
      const n = data?.created || 1;
      toast.success(n > 1 ? `${n} recurring visits submitted for approval` : "Visit request submitted for approval");
      onCreated?.(data);
      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="visit-request-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <CalendarPlus className="h-5 w-5 text-[#0d9488]" /> Request a Visit
          </DialogTitle>
          <DialogDescription>Fill in the details below to submit a visit request for approval.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Child's name</Label>
            <Input data-testid="visit-child-input" value={form.child_name} onChange={set("child_name")} required placeholder="Emma Carter" />
          </div>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" data-testid="visit-date-input" value={form.visit_date} onChange={set("visit_date")} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start time</Label>
              <Input type="time" data-testid="visit-start-input" value={form.start_time} onChange={set("start_time")} required />
            </div>
            <div className="space-y-1.5">
              <Label>End time</Label>
              <Input type="time" data-testid="visit-end-input" value={form.end_time} onChange={set("end_time")} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Visit type</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="visit-type-toggle">
              {[
                { v: "supervised", l: "Supervised" },
                { v: "unsupervised", l: "Unsupervised" },
              ].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  data-testid={`visit-type-${o.v}`}
                  onClick={() => setForm({ ...form, visit_type: o.v })}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                    form.visit_type === o.v
                      ? "border-[#1e3a8a] bg-[#1e3a8a] text-white"
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Input data-testid="visit-location-input" value={form.location} onChange={set("location")} required placeholder="Family Center, Room 3" />
          </div>
          <div className="space-y-1.5">
            <Label>Repeat</Label>
            <div className="grid grid-cols-4 gap-2" data-testid="recurrence-toggle">
              {[
                { v: "none", l: "One-time" },
                { v: "weekly", l: "Weekly" },
                { v: "biweekly", l: "Biweekly" },
                { v: "monthly", l: "Monthly" },
              ].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  data-testid={`recurrence-${o.v}`}
                  onClick={() => setForm({ ...form, recurrence: o.v })}
                  className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                    form.recurrence === o.v
                      ? "border-[#0d9488] bg-[#0d9488] text-white"
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          {form.recurrence !== "none" && (
            <div className="space-y-1.5">
              <Label>Number of visits</Label>
              <Input
                type="number"
                min="2"
                max="26"
                data-testid="occurrences-input"
                value={form.occurrences}
                onChange={set("occurrences")}
              />
              <p className="text-xs text-slate-400">
                Creates {form.occurrences || 0} {form.recurrence} visits starting from the selected date. Each needs approval.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea data-testid="visit-notes-input" value={form.notes} onChange={set("notes")} placeholder="Any details for the supervisor..." rows={2} />
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="submit-visit-btn" disabled={loading} className="w-full bg-[#1e3a8a] hover:bg-[#172554]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
